/**
 * useRealtimeTables — suscripción a varias tablas en un único channel.
 *
 * Reemplaza el patrón de armar un channel por tabla. Acepta una lista de
 * `{ table, filter? }` y un callback que recibe la tabla que disparó el
 * evento + el payload nativo. Internamente:
 *
 *   - Crea un único `supabase.channel(channelKey)` y le adjunta una
 *     suscripción `postgres_changes` por cada entrada.
 *   - Limpia el channel al desmontar o al cambiar dependencias.
 *   - Devuelve `connected` para que la UI muestre indicador (opcional).
 *
 * Por convención `channelKey` debería incluir un scope (ej. la empresa)
 * para no chocar entre vistas — pero los filtros por columna ya hacen el
 * trabajo pesado.
 *
 * Patrón aplicable: P-FE-04 (Realtime > polling). Memory:
 * "Las vistas escuchan Supabase Realtime y se actualizan solas."
 */

import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type {
  RealtimePostgresChangesPayload,
  RealtimeChannel,
} from '@supabase/supabase-js';

export type RealtimeEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

export interface RealtimeSubscription {
  table: string;
  /** Default '*' (INSERT + UPDATE + DELETE). */
  event?: RealtimeEvent;
  /**
   * Filtro tipo `empresa_id=eq.<uuid>`.
   * Si no se provee, escuchamos toda la tabla — RLS sigue corriendo
   * server-side igual.
   */
  filter?: string;
  schema?: string;
}

export type RealtimePayload = RealtimePostgresChangesPayload<Record<string, unknown>>;

type Callback = (table: string, payload: RealtimePayload) => void;

/**
 * Hook principal. `enabled=false` corta la suscripción (útil cuando aún
 * no se conoce el scope, ej. sin empresa elegida).
 */
export function useRealtimeTables(
  channelKey: string,
  subs: RealtimeSubscription[],
  onChange: Callback,
  enabled = true,
): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  // Mantenemos el callback en una ref para no recrear el channel si la
  // función cambia identidad por re-render.
  const cbRef = useRef<Callback>(onChange);
  useEffect(() => {
    cbRef.current = onChange;
  }, [onChange]);

  // Serializamos `subs` para usar como dep estable.
  const subsKey = JSON.stringify(subs);

  useEffect(() => {
    if (!enabled || subs.length === 0) return;

    let channel: RealtimeChannel | null = supabase.channel(channelKey);

    for (const s of subs) {
      channel = channel.on(
        'postgres_changes',
        {
          event: s.event ?? '*',
          schema: s.schema ?? 'public',
          table: s.table,
          ...(s.filter ? { filter: s.filter } : {}),
        },
        (payload: RealtimePayload) => {
          cbRef.current(s.table, payload);
        },
      );
    }

    channel.subscribe((status) => {
      setConnected(status === 'SUBSCRIBED');
    });

    return () => {
      setConnected(false);
      if (channel) {
        supabase.removeChannel(channel);
        channel = null;
      }
    };
    // subsKey + channelKey + enabled cubren cambios reales.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelKey, subsKey, enabled]);

  return { connected };
}
